/**
 * +prof: Auto gathering (docs/design/professions.md §7). The Auto setting Hunt / Gather / Both:
 *   - hunt: never gathers (the default; the grind as before);
 *   - both: between pulls, a ready node it can gather within GATHER_BOTH_YD (inside its home) beats
 *     the next hunt; it walks over, stands still and channels it;
 *   - gather: only gathers. It still fights whatever engages it, but never hunts; with nothing ready
 *     it walks toward the node that will be ready soonest.
 * Nodes with a hostile mob standing near them (not engaged yet) are skipped, so low-level Auto never
 * pulls a camp for a herb. Skill gating comes from the sim (setGatherSkill), like a player's tooltip.
 */
import type { Command, DeepReadonly, FieldDef, GatherNodeDef, Ms, Vec2 } from '@keystone/sim';
import { GATHER_REACH, gatherNodeReady, gatherSkillOf } from '@keystone/sim';
import { dist } from './geometry';
import type { BotMemory } from './memory';
import { simWorld, type Perception } from './perception';

export type GatherMode = 'hunt' | 'gather' | 'both';

/** Both: nodes within this distance (and in Auto's home) are worth the detour. */
export const GATHER_BOTH_YD = 30;
/** Gather mode: how far the bot looks for nodes (still inside its home). */
export const GATHER_SEEK_YD = 140;
/** Skip a node with a hostile mob (not yet fighting us) this close to it. */
export const GATHER_MOB_CLEAR_YD = 11;
/** A node the bot failed to reach / start is left alone this long. */
const AVOID_MS: Ms = 45_000;
/** Interact retries before a node is avoided. */
const MAX_TRIES = 3;

export interface GatherMemory {
  mode: GatherMode;
  /** The node being walked to / gathered. */
  target: string | null;
  tries: number;
  avoid: Map<string, Ms>;
}

export function createGatherMemory(mode: GatherMode = 'hunt'): GatherMemory {
  return { mode, target: null, tries: 0, avoid: new Map() };
}

type RField = DeepReadonly<FieldDef>;
type Node = DeepReadonly<GatherNodeDef>;

/** The hero is channelling a gather right now. */
export function channelling(p: Perception): boolean {
  return !!simWorld(p).field?.gather?.channels[p.self.id];
}

/**
 * The node to gather now (null = none worth it). `inHome` keeps Auto on its ground; `nearPortal`
 * keeps it off portals. Updates `g.target`.
 */
export function pickGatherNode(p: Perception, g: GatherMemory, field: RField | undefined, inHome: (at: Vec2) => boolean, nearPortal: (at: Vec2) => boolean): Node | null {
  if (g.mode === 'hunt' || !field?.gatherNodes?.length) {
    g.target = null;
    return null;
  }
  const w = simWorld(p);
  const self = p.self;
  for (const [id, until] of g.avoid) if (until <= p.now) g.avoid.delete(id);
  const usable = (n: Node): boolean =>
    gatherNodeReady(w, n.id, p.now) &&
    gatherSkillOf(w, self.id, n.profession) >= n.req &&
    !g.avoid.has(n.id) &&
    inHome(n.pos) &&
    !nearPortal(n.pos) &&
    !p.enemies.some((e) => e.alive && !p.engaged.includes(e) && dist(e.pos, n.pos) < GATHER_MOB_CLEAR_YD);
  const cur = g.target ? field.gatherNodes.find((n) => n.id === g.target) : undefined;
  if (cur && usable(cur)) return cur;
  g.target = null;
  g.tries = 0;
  const reach = g.mode === 'both' ? GATHER_BOTH_YD : GATHER_SEEK_YD;
  let best: Node | null = null;
  let bestScore = Infinity;
  for (const n of field.gatherNodes) {
    const d = dist(n.pos, self.pos);
    if (d > reach || !usable(n)) continue;
    // Skill-up colour: grey nodes (skill ≥ req + 60) only when nothing else is near.
    const over = gatherSkillOf(w, self.id, n.profession) - n.req;
    const score = d + (over >= 60 ? 40 : over >= 40 ? 12 : 0) - n.tier * 2;
    if (score < bestScore) {
      bestScore = score;
      best = n;
    }
  }
  g.target = best?.id ?? null;
  return best;
}

/**
 * Commands to gather `node`: walk up to it, stop, interact; hold still while the channel runs.
 * Returns null when the node turned out unreachable (the caller moves on).
 */
export function planGather(p: Perception, mem: BotMemory, g: GatherMemory, node: Node, move: (dest: Vec2) => Command[] | null): Command[] {
  const self = p.self;
  if (channelling(p)) {
    mem.lastMoveDest = null;
    return [];
  }
  const d = dist(self.pos, node.pos);
  if (d <= GATHER_REACH - 0.7) {
    if (self.move) {
      mem.lastMoveDest = null;
      return [{ type: 'stop', actorId: self.id }];
    }
    g.tries++;
    if (g.tries > MAX_TRIES) {
      g.avoid.set(node.id, p.now + AVOID_MS);
      g.target = null;
      return [];
    }
    return [{ type: 'interact', actorId: self.id, what: `gather:${node.id}` }];
  }
  // Stand a step short of the node, on the side we come from.
  const k = Math.max(0, d - 1.6) / d;
  const dest = { x: self.pos.x + (node.pos.x - self.pos.x) * k, y: self.pos.y + (node.pos.y - self.pos.y) * k };
  const cmds = move(dest);
  if (cmds === null) {
    g.avoid.set(node.id, p.now + AVOID_MS);
    g.target = null;
    return [];
  }
  return cmds;
}

/** Gather mode with nothing ready: the node in reach that comes back soonest (walk there and wait). */
export function nextGatherNode(p: Perception, field: RField | undefined, inHome: (at: Vec2) => boolean): Node | null {
  const w = simWorld(p);
  let best: Node | null = null;
  let bestT = Infinity;
  for (const n of field?.gatherNodes ?? []) {
    if (gatherSkillOf(w, p.self.id, n.profession) < n.req || !inHome(n.pos)) continue;
    const readyAt = w.field?.gather?.nodes[n.id]?.readyAt ?? 0;
    const t = Math.max(readyAt - p.now, (dist(n.pos, p.self.pos) / 7) * 1000);
    if (t < bestT) {
      bestT = t;
      best = n;
    }
  }
  return best;
}
